import { useCallback, useEffect, useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
// Note: PostgrestFilterBuilder typing avoided to skip extra dep; use `any`.
import { supabase } from "@/integrations/supabase/client";
import { useUrlState } from "@/lib/urlState";
import { DEFAULT_PAGE_SIZE, type PageSize } from "@/lib/constants";

export interface UsePaginatedQueryOptions<TRow> {
  /** Stable key prefix (use `queryKeys.<entity>.list` style). */
  queryKey: readonly unknown[];
  /** Supabase table or view name. */
  table: string;
  /** PostgREST select string. Defaults to `*`. */
  select?: string;
  /** Optional builder transform — apply filters / search / joins. */
  filters?: (q: any) => any;
  /** Sort column. */
  orderBy?: { column: string; ascending?: boolean };
  /** Initial page size (defaults to 25). */
  initialPageSize?: PageSize;
  /** Initial page (0-indexed). */
  initialPage?: number;
  /** Sync page/pageSize to URL `?page=&pageSize=`. */
  syncToUrl?: boolean;
  /** Honor `deleted_at IS NULL` (Phase 3 soft-delete). */
  honorSoftDelete?: boolean;
  /** Disable the query. */
  enabled?: boolean;
  /** Map raw row to typed row. */
  mapRow?: (row: any) => TRow;
}

export interface UsePaginatedQueryResult<TRow> {
  rows: TRow[];
  total: number;
  page: number;
  pageSize: PageSize;
  pageCount: number;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  error: unknown;
  setPage: (page: number) => void;
  setPageSize: (size: PageSize) => void;
  refetch: () => void;
}

/**
 * Server-side paginated Supabase query with optional URL-state sync.
 * Always uses `.range()` (Supabase 1000-row default does not apply).
 */
export function usePaginatedQuery<TRow = any>(
  opts: UsePaginatedQueryOptions<TRow>,
): UsePaginatedQueryResult<TRow> {
  const {
    queryKey,
    table,
    select = "*",
    filters,
    orderBy,
    initialPageSize = DEFAULT_PAGE_SIZE,
    initialPage = 0,
    syncToUrl = false,
    honorSoftDelete = false,
    enabled = true,
    mapRow,
  } = opts;

  const url = useUrlState();
  const urlPage = syncToUrl ? url.getNumber("page", initialPage) : initialPage;
  const urlPageSize = syncToUrl
    ? (url.getNumber("pageSize", initialPageSize) as PageSize)
    : initialPageSize;

  const [page, setPageState] = useState<number>(urlPage);
  const [pageSize, setPageSizeState] = useState<PageSize>(urlPageSize);

  // Keep local state in sync if URL changes externally (back/forward).
  useEffect(() => {
    if (!syncToUrl) return;
    setPageState(urlPage);
    setPageSizeState(urlPageSize);
     
  }, [syncToUrl, urlPage, urlPageSize]);

  const setPage = useCallback(
    (next: number) => {
      setPageState(next);
      if (syncToUrl) url.set({ page: next });
    },
    [syncToUrl, url],
  );

  const setPageSize = useCallback(
    (next: PageSize) => {
      setPageSizeState(next);
      setPageState(0);
      if (syncToUrl) url.set({ pageSize: next, page: 0 });
    },
    [syncToUrl, url],
  );

  const fullKey = useMemo(
    () => [...queryKey, { page, pageSize }] as const,
    [queryKey, page, pageSize],
  );

  const query = useQuery({
    queryKey: fullKey,
    enabled,
    placeholderData: keepPreviousData,
    queryFn: async () => {
      let q = (supabase.from as any)(table).select(select, { count: "exact" });
      if (honorSoftDelete) q = q.is("deleted_at", null);
      if (filters) q = filters(q as any) as any;
      if (orderBy) {
        q = q.order(orderBy.column, { ascending: orderBy.ascending ?? false });
      }
      const from = page * pageSize;
      const to = from + pageSize - 1;
      const { data, error, count } = await q.range(from, to);
      if (error) throw error;
      const rows = (data ?? []) as any[];
      return {
        rows: (mapRow ? rows.map(mapRow) : (rows as TRow[])) as TRow[],
        total: count ?? 0,
      };
    },
  });

  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return {
    rows: query.data?.rows ?? [],
    total,
    page,
    pageSize,
    pageCount,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    setPage,
    setPageSize,
    refetch: () => void query.refetch(),
  };
}
